import {PropertyObject, signal} from "../lib";
import {IDocument, IDocumentCommit, IDocumentTransaction} from "../IMemoRemote";
import {PropertyObjectWithId} from "../lib/memoTypes";

let globIdCounter = 1;
function getNextId() {
    return `id-${Date.now().toString(36)}-${(++globIdCounter).toString(36)}${Math.random().toString(36).substring(2, 8)}`;
}

export class InMemoryDocument implements IWritableDocument {
    readonly documentId: string;
    readonly items: Record<string, Readonly<PropertyObject>>;
    readonly ready: Promise<void>;
    readonly updated = signal<(c: IDocumentCommit) => void>();

    constructor() {
        this.documentId = "temporary/" + getNextId();
        this.items = {};
        this.ready = Promise.resolve();
    }

    get readonly() {
        return false;
    }

    sendAll() {
        throw new Error('Method not implemented.');
    }

    transact(): IDocumentTransaction {
        return new DocumentTransaction(this);
    }

    async performChanges(changes: PropertyObjectWithId[]): Promise<void> {
        for (const newdata of changes) {
            this.items[newdata.id] = Object.freeze(newdata);
        }
        this.updated.emit({items: changes});
    }

}

export class DocumentTransaction implements IDocumentTransaction {
    private operations: PropertyObjectWithId[] = [];
    private readonly t : number;
    constructor(private doc: IWritableDocument) {
        this.t = +(new Date());
    }
    replace(props: PropertyObject): IDocumentTransaction {
        if (typeof props.id === 'undefined') props.id = getNextId();
        if (typeof props.id !== 'string' || !props.id) throw new Error("props.id needs to be a non-empty string");
        props.lmt = this.t;
        props.t = this.t;
        this.operations.push(structuredClone(props) as PropertyObjectWithId);
        return this;
    }
    update(props: PropertyObject): IDocumentTransaction {
        if (typeof props.id === 'undefined') {
            props.id = getNextId();
        } else {
            if (typeof props.id !== 'string' || !props.id) throw new Error("props.id needs to be a non-empty string");
            const oldProps = this.doc.items[props.id];
            if (oldProps) {
                props = Object.assign({}, oldProps, props);
                for (const k in props) {
                    if (props[k] === undefined) delete props[k];
                }
            }
        }
        props.lmt = this.t;
        props.t = this.t;
        this.operations.push(structuredClone(props) as PropertyObjectWithId);
        return this;
    }

    async commit(): Promise<void> {
        await this.doc.performChanges(this.operations);
    }
}

export interface IWritableDocument extends IDocument {
    performChanges(changes: PropertyObjectWithId[]): Promise<void>;
}

export class ReadonlyDocumentError extends Error {
    constructor() {
        super("Document is readonly.");
    }
}

export class ReadonlyDocument implements IDocument {
    constructor(private readonly backingDocument: IDocument) {
    }
    get documentId() {
        return this.backingDocument.documentId;
    }
    get items() {
        return this.backingDocument.items;
    }
    get ready() {
        return this.backingDocument.ready;
    }
    get updated() {
        return this.backingDocument.updated;
    }
    get readonly() {
        return true;
    }

    transact(): IDocumentTransaction {
        throw new ReadonlyDocumentError();
    }

    sendAll() {
        throw new Error('Method not implemented.');
    }
}
